import React from 'react';
import { ListRenderItem, StyleSheet, View } from 'react-native';

import { UserAvatar } from '@components/Avatar';
import { Button } from '@components/Button';
import FlatList from '@components/FlatList';
import Placeholder, { EmptySearchPlaceholder } from '@components/Placeholders';
import { PressableOpacity } from '@components/Pressable';
import ScreenContainer from '@components/ScreenContainer';
import SearchBar from '@components/SearchBar';
import Surface from '@components/Surface';
import { Caption, Subheading } from '@components/Typography';
import { Row } from '@components/View';
import { useContacts } from '@context/ContactsContext';
import { SHARE_MESSAGE } from '@helpers/Constants';
import { openSettings } from '@helpers/Linking';
import { useLocalSearch } from '@helpers/LocalSearch';
import { logError } from '@helpers/Loggers';
import { sendSMSMessage } from '@helpers/SMS';
import { DeviceContact } from '@types';

const CONTACT_SEARCH_KEYS = ['displayName'];

export default function InviteContactsScreen() {
  const { contacts, requestPermissionAndSync } = useContacts();
  const [searchText, setSearchText] = React.useState<string>('');
  const { loading: searchLoading, results: filteredContacts } = useLocalSearch<DeviceContact>({
    items: contacts,
    keys: CONTACT_SEARCH_KEYS,
    query: searchText,
  });

  React.useEffect(() => {
    requestPermissionAndSync().catch((error) => logError(error));
  }, [requestPermissionAndSync]);

  const tryOpenSettings = async () => {
    try {
      await openSettings();
    } catch (error) {
      logError(error);
    }
  };

  const keyExtractor = (item: DeviceContact, index: number) => item.contactId;

  const renderContact: ListRenderItem<DeviceContact> = ({ item }) => {
    const onPress = async () => {
      try {
        await sendSMSMessage(SHARE_MESSAGE, item.phoneNumber);
      } catch (error) {
        logError(error);
      }
    };

    return (
      <PressableOpacity onPress={onPress}>
        <Surface style={styles.contactSurface}>
          <Row style={styles.contactRow}>
            <Row>
              <View style={styles.contactAvatar}>
                <UserAvatar displayName={item.displayName} photoUrl={item.photoUrl} size={50} />
              </View>
              <View>
                <Subheading numberOfLines={1}>{item.displayName}</Subheading>
                <Caption numberOfLines={1}>{item.phoneNumber}</Caption>
              </View>
            </Row>
            <View style={styles.contactButton}>
              <Button onPress={onPress} mode="contained" uppercase={false}>
                Invite
              </Button>
            </View>
          </Row>
        </Surface>
      </PressableOpacity>
    );
  };

  const renderSearchBar = () => (
    <View style={styles.searchBarContainer}>
      <SearchBar
        value={searchText}
        onChangeText={(text) => setSearchText(text)}
        loading={searchLoading}
        placeholder="Search by name"
      />
    </View>
  );

  const renderEmptyComponent = () =>
    searchText.length > 0 && filteredContacts.length === 0 ? (
      <EmptySearchPlaceholder />
    ) : (
      <Placeholder
        title="Joinstar is better with friends!"
        icon="account-plus"
        action="Enable Contacts"
        onPress={tryOpenSettings}
      />
    );

  return (
    <ScreenContainer>
      <FlatList
        data={filteredContacts}
        renderItem={renderContact}
        keyExtractor={keyExtractor}
        style={styles.list}
        ListHeaderComponent={renderSearchBar()}
        stickyHeaderIndices={[0]}
        ListEmptyComponent={renderEmptyComponent()}
        initialNumToRender={10}
        maxToRenderPerBatch={10}
        windowSize={5}
      />
    </ScreenContainer>
  );
}

const styles = StyleSheet.create({
  contactAvatar: {
    paddingRight: 10,
  },
  contactButton: {
    marginRight: 5,
  },
  contactRow: {
    justifyContent: 'space-between',
  },
  contactSurface: {
    margin: 5,
    padding: 10,
  },
  list: {
    width: '100%',
  },
  searchBarContainer: {
    marginBottom: 5,
    marginHorizontal: 5,
    marginTop: 10,
  },
});
